import { useEffect, useRef } from 'react'

const FOCUSABLE = 'button:not([disabled]), [tabindex]:not([tabindex="-1"])'

const ConfirmModal = ({
  title = 'Confirm',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  const dialogRef = useRef(null)
  const previousFocusRef = useRef(null)
  // The latest `onCancel`, read by the mount-only effect below.
  const onCancelRef = useRef(onCancel)
  useEffect(() => { onCancelRef.current = onCancel })

  const confirmClass = variant === 'danger'
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-cb-navy hover:bg-cb-navy/90 text-white'

  /*
   * Initial focus, focus trap, Escape and focus restore: MOUNT ONLY.
   *
   * This used to depend on [onCancel]. Hosts pass an inline arrow, so every host re-render
   * re-ran it and focused the confirm button again. The Report Queue re-renders every second
   * while a run is live and polls every five: measured on QA (2026-10-14), focus moved to
   * "Keep it" was back on the destructive "Dismiss" within 0.4 s, so a keyboard user's Enter
   * dismissed the run they meant to keep. Modal.jsx fixed the same shape earlier; this follows it.
   */
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return undefined
    previousFocusRef.current = document.activeElement
    const focusables = () => dialog.querySelectorAll(FOCUSABLE)

    // Focus the LAST focusable element (the confirm action), deliberately — several pages
    // depend on this order (Modal.jsx's header comment). The old comment said "first".
    const initial = focusables()
    if (initial.length) initial[initial.length - 1].focus()
    let lastInside = dialog.contains(document.activeElement) ? document.activeElement : null

    const onKey = (e) => {
      if (e.key === 'Escape') { onCancelRef.current?.(); return }
      if (e.key !== 'Tab') return
      const list = focusables()
      if (!list.length) return
      const first = list[0]
      const last = list[list.length - 1]
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }

    /*
     * Something outside moved focus while this dialog is open. The job drawer does: it closes in
     * the same click that opens this confirm, but React renders its close AFTER this has mounted
     * (the router's update is a transition), and it then restores focus to whatever opened it.
     * Take focus back, and adopt that element as where focus goes when this closes — it is where
     * the page itself wants focus to be, and the element remembered at mount (the drawer's own
     * kebab) is gone by then.
     */
    const onFocusIn = (e) => {
      if (dialog.contains(e.target)) { lastInside = e.target; return }
      previousFocusRef.current = e.target
      const list = focusables()
      const back = lastInside && dialog.contains(lastInside) ? lastInside : list[list.length - 1]
      back?.focus()
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('focusin', onFocusIn)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('focusin', onFocusIn)
      // Only an element still in the page: focusing a detached one does nothing at all.
      const target = previousFocusRef.current
      if (target?.isConnected) target.focus()
    }
  }, [])

  return (
    /*
     * The backdrop must NOT be aria-hidden: it WRAPS the dialog, and aria-hidden on an
     * ancestor removes the whole subtree from the accessibility tree — the title, the message
     * and both buttons were invisible to screen readers on every page that confirmed
     * anything. `aria-modal` on the dialog is what tells assistive technology to treat the
     * rest of the page as inert. A click on the backdrop still cancels.
     */
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        aria-describedby="confirm-modal-message"
        className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="confirm-modal-title" className="text-lg font-display font-medium text-gray-900 mb-2">
          {title}
        </h3>
        <p id="confirm-modal-message" className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors rounded-lg hover:bg-gray-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm disabled:opacity-50 ${confirmClass}`}
          >
            {isLoading ? 'Processing...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConfirmModal
